import { StoreApi } from 'zustand';

import {
  TLocale,
  TRegionInstance,
  TTextLocalized,
} from '@northladder/utilities';

import {
  IAppConfigBuyer,
  TCompanyId,
  TThemeColorScheme,
} from '../app-config-store';

export interface ITermsAndConditions {
  filePath: string;
  version: number;
}

type TWorkingHoursDay =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

type TWorkingHoursPeriod = 'am' | 'pm';

export interface IWorkingHours {
  days: {
    startDay: TWorkingHoursDay;
    endDay: TWorkingHoursDay;
  };
  time: {
    startTime: string;
    startPeriod: TWorkingHoursPeriod;
    endTime: string;
    endPeriod: TWorkingHoursPeriod;
  };
}

/**
 * This represents the default configuration for the Common Apps module.
 */
export interface IAppsCommonConfig {
  colorScheme: TThemeColorScheme;
  companyId: TCompanyId;
  companyName: string;
  locales: TLocale[];
}

/**
 * This displays each record in the public.json config that identifies all the
 * available tenant environments.
 */
export interface ITenantEnvironment {
  id: string;
  identifier: string;
  label: TTextLocalized;
  logoUrl: string;
}

export interface ICoordinate {
  lat: number;
  lng: number;
  zoom?: number;
  /**
   * The formattedName/name that appears on the map location item.
   */
  title?: string;
  titleHtml?: string;
}

export const DEFAULT_LOCATION: ICoordinate = {
  lat: 25.2048,
  lng: 55.2708,
  title: 'Dubai, UAE',
};

/**
 * The details of a particular tenant configuration.
 */
export interface ITenantConfig {
  address: string;
  apiBaseURL: string;
  buyerConfig: IAppConfigBuyer | null;
  buyerAppUrl: string;
  cdnBaseUrl: string;
  commonAppConfig: IAppsCommonConfig;
  contactNumber: string;
  coordinates: ICoordinate;
  countryCode: string;
  currency: string;
  email: string;
  emailOTPForOnboarding: boolean;
  identifier: string;
  label: TTextLocalized;
  logoUrl: string;
  tenantId: string;
  termsAndConditions: ITermsAndConditions | null;
  whatsappNumber: string;
  workingHours: IWorkingHours | null;
}

/**
 * -----------------------------------------------------------------------------
 * These types are used for tracking stateful logic for the Region Store
 */
export interface IRegionState {
  appLanguage?: TLocale;
  region: TRegionInstance | null;
  tenantConfig: ITenantConfig | null;
  updateAppLanguage: (language: TLocale) => void;
  updateActiveRegion: (region: TRegionInstance) => void;
  onUpdateTenantConfig: (tenantConfig: ITenantConfig | null) => void;
}

export type TSetRegionState = StoreApi<IRegionState>['setState'];
